import { tokenize } from '@corp/helpers/string'

const ESCAPE_REGEX = /[-/\\^$*+?.()|[\]{}]/g

// Extract the value of the specified property from an object,
// or simply return the item if no property is given.
function getSearchableValue<T> (item: T, searchableProp: keyof T) {
  return searchableProp ? item[searchableProp] : item
}

// Internal: Build a regular expression for the given string,
// escaping where necessary and specifying case insensitivity by default.
export function buildRegExp (token: string) {
  return new RegExp(`(${token.replace(ESCAPE_REGEX, '\\$&')})`, 'i')
}

// Internal: Tokenize the query and sort the tokens from longest to shortest.
// It's important that we start with the largest tokens, because we want to skip
// ahead as far as possible when a match is found, but we don't want to skip
// matching any smaller substrings in the process.
export function sortedTokensFor (query: string) {
  return tokenize(query).sort((a, b) => b.length - a.length)
}

// Public: Tokenizes the string, and returns an Array of Regex, sorted from
// largest to smallest.
export function tokenMatcherFor (query: string) {
  const tokens = sortedTokensFor(query).map(buildRegExp)
  return (text: any) => tokens.every(tokenRegex => tokenRegex.test(text))
}

// Public: Filter a list of items given a query string and (optionally) a
// searchable prop, which signifies the item property to compare against the
// search query. If no searchable prop is given, the query will be compared
// against the item itself.
export function filterItems<T> (items: T[], { query, searchableProp }: { query: string; searchableProp: keyof T }) {
  if (!query) return items

  const matchesTokensInQuery = tokenMatcherFor(query)
  return items.filter(item => matchesTokensInQuery(getSearchableValue(item, searchableProp)))
}
